import React, { useState, useEffect, useRef, useCallback } from "react";
import SearchBar from "../navbar/SearchBar";
import { searchService } from "../../services/searchService";
import { getApiBaseUrl } from "../../apiBaseUrlFinder";
import "./compareManagers.css";

const CompareManagers = () => {
  // Manager 1 state
  const [manager1, setManager1] = useState(null);
  const [manager1Data, setManager1Data] = useState(null);
  const [manager1Loading, setManager1Loading] = useState(false);
  const [search1Term, setSearch1Term] = useState("");
  const [search1Suggestions, setSearch1Suggestions] = useState([]);
  const [search1LastKey, setSearch1LastKey] = useState(null);
  const [search1DropdownVisible, setSearch1DropdownVisible] = useState(false);
  const [search1IsDropdownOpen, setSearch1IsDropdownOpen] = useState(false);
  const search1WrapperRef = useRef(null);
  const search1Ref = useRef(null);
  const search1DropdownRef = useRef(null);
  const [search1DebouncedTerm, setSearch1DebouncedTerm] = useState("");
  const search1LastKeyRef = useRef(null);
  const search1LoadingRef = useRef(false);

  // Manager 2 state
  const [manager2, setManager2] = useState(null);
  const [manager2Data, setManager2Data] = useState(null);
  const [manager2Loading, setManager2Loading] = useState(false);
  const [search2Term, setSearch2Term] = useState("");
  const [search2Suggestions, setSearch2Suggestions] = useState([]);
  const [search2LastKey, setSearch2LastKey] = useState(null);
  const [search2DropdownVisible, setSearch2DropdownVisible] = useState(false);
  const [search2IsDropdownOpen, setSearch2IsDropdownOpen] = useState(false);
  const search2WrapperRef = useRef(null);
  const search2Ref = useRef(null);
  const search2DropdownRef = useRef(null);
  const [search2DebouncedTerm, setSearch2DebouncedTerm] = useState("");
  const search2LastKeyRef = useRef(null);
  const search2LoadingRef = useRef(false);

  // Debounce search terms
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch1DebouncedTerm(search1Term);
    }, 300);
    return () => clearTimeout(handler);
  }, [search1Term]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch2DebouncedTerm(search2Term);
    }, 300);
    return () => clearTimeout(handler);
  }, [search2Term]);

  // Fetch suggestions for search 1
  const fetchSearch1Suggestions = useCallback(
    async (query, append = false) => {
      if (!query.trim()) {
        setSearch1Suggestions([]);
        search1LastKeyRef.current = null;
        setSearch1LastKey(null);
        return;
      }

      // Prevent multiple simultaneous calls
      if (search1LoadingRef.current) {
        return;
      }

      search1LoadingRef.current = true;

      try {
        // Reset lastKey for new searches (not append)
        if (!append) {
          search1LastKeyRef.current = null;
        }
        const lastKeyToUse = append ? search1LastKeyRef.current : null;
        const result = await searchService.searchManagers(query, lastKeyToUse);

        setSearch1Suggestions((prev) =>
          append ? [...prev, ...result.managers] : result.managers
        );
        search1LastKeyRef.current = result.lastKey;
        setSearch1LastKey(result.lastKey);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSearch1Suggestions([]);
        search1LastKeyRef.current = null;
        setSearch1LastKey(null);
      } finally {
        search1LoadingRef.current = false;
      }
    },
    []
  );

  // Fetch suggestions for search 2
  const fetchSearch2Suggestions = useCallback(
    async (query, append = false) => {
      if (!query.trim()) {
        setSearch2Suggestions([]);
        search2LastKeyRef.current = null;
        setSearch2LastKey(null);
        return;
      }

      // Prevent multiple simultaneous calls
      if (search2LoadingRef.current) {
        return;
      }

      search2LoadingRef.current = true;

      try {
        // Reset lastKey for new searches (not append)
        if (!append) {
          search2LastKeyRef.current = null;
        }
        const lastKeyToUse = append ? search2LastKeyRef.current : null;
        const result = await searchService.searchManagers(query, lastKeyToUse);

        setSearch2Suggestions((prev) =>
          append ? [...prev, ...result.managers] : result.managers
        );
        search2LastKeyRef.current = result.lastKey;
        setSearch2LastKey(result.lastKey);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSearch2Suggestions([]);
        search2LastKeyRef.current = null;
        setSearch2LastKey(null);
      } finally {
        search2LoadingRef.current = false;
      }
    },
    []
  );

  // Fetch suggestions when debounced terms change
  useEffect(() => {
    if (search1DebouncedTerm.trim() && !manager1) {
      fetchSearch1Suggestions(search1DebouncedTerm);
      setSearch1DropdownVisible(true);
    } else {
      setSearch1Suggestions([]);
      setSearch1DropdownVisible(false);
    }
  }, [search1DebouncedTerm, manager1, fetchSearch1Suggestions]);

  useEffect(() => {
    if (search2DebouncedTerm.trim() && !manager2) {
      fetchSearch2Suggestions(search2DebouncedTerm);
      setSearch2DropdownVisible(true);
    } else {
      setSearch2Suggestions([]);
      setSearch2DropdownVisible(false);
    }
  }, [search2DebouncedTerm, manager2, fetchSearch2Suggestions]);

  // Fetch manager profile data
  const fetchManagerProfile = async (
    managerId,
    managerName,
    setManagerData,
    setLoading
  ) => {
    if (!managerId || !managerName) return;

    setLoading(true);
    try {
      const apiUrl = `${getApiBaseUrl()}/manager_profile?manager_id=${encodeURIComponent(
        managerId
      )}&manager_full_name=${encodeURIComponent(managerName)}`;

      const response = await fetch(apiUrl);
      const data = await response.json();
      setManagerData(data);
    } catch (error) {
      console.error("Error fetching manager profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle suggestion click for manager 1
  const handleSearch1SuggestionClick = (item) => {
    if (item.id === "clear") {
      setSearch1Suggestions([]);
      search1LastKeyRef.current = null;
      setSearch1LastKey(null);
      return;
    }

    setManager1(item);
    setSearch1Term(item.name);
    setSearch1Suggestions([]);
    setSearch1DropdownVisible(false);
    // Reset lastKey when manager is selected
    search1LastKeyRef.current = null;
    setSearch1LastKey(null);
    fetchManagerProfile(
      item.id,
      item.name,
      setManager1Data,
      setManager1Loading
    );
  };

  // Handle suggestion click for manager 2
  const handleSearch2SuggestionClick = (item) => {
    if (item.id === "clear") {
      setSearch2Suggestions([]);
      search2LastKeyRef.current = null;
      setSearch2LastKey(null);
      return;
    }

    setManager2(item);
    setSearch2Term(item.name);
    setSearch2Suggestions([]);
    setSearch2DropdownVisible(false);
    // Reset lastKey when manager is selected
    search2LastKeyRef.current = null;
    setSearch2LastKey(null);
    fetchManagerProfile(
      item.id,
      item.name,
      setManager2Data,
      setManager2Loading
    );
  };

  // Handle search input changes
  const handleSearch1Change = (e) => {
    setSearch1Term(e.target.value);
    // Reset lastKey when search term changes
    search1LastKeyRef.current = null;
    setSearch1LastKey(null);
    if (manager1) {
      setManager1(null);
      setManager1Data(null);
    }
  };

  const handleSearch2Change = (e) => {
    setSearch2Term(e.target.value);
    // Reset lastKey when search term changes
    search2LastKeyRef.current = null;
    setSearch2LastKey(null);
    if (manager2) {
      setManager2(null);
      setManager2Data(null);
    }
  };

  // Handle search requests
  const handleSearch1Request = () => {
    if (search1Term.trim()) {
      fetchSearch1Suggestions(search1Term);
      setSearch1DropdownVisible(true);
    }
  };

  const handleSearch2Request = () => {
    if (search2Term.trim()) {
      fetchSearch2Suggestions(search2Term);
      setSearch2DropdownVisible(true);
    }
  };

  // Handle view more for search 1
  const handleSearch1ViewMore = async () => {
    if (search1Term.trim() && search1LastKeyRef.current && !search1LoadingRef.current) {
      await fetchSearch1Suggestions(search1Term, true);
    }
  };

  // Handle view more for search 2
  const handleSearch2ViewMore = async () => {
    if (search2Term.trim() && search2LastKeyRef.current && !search2LoadingRef.current) {
      await fetchSearch2Suggestions(search2Term, true);
    }
  };

  // Handle outside clicks for search 1
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        search1WrapperRef.current &&
        !search1WrapperRef.current.contains(e.target)
      ) {
        const isSuggestionClick =
          e.target.closest(".suggestion-box") ||
          e.target.closest(".suggestion-item") ||
          e.target.textContent === "View More";

        if (!isSuggestionClick) {
          setTimeout(() => {
            setSearch1Suggestions([]);
            setSearch1DropdownVisible(false);
          }, 100);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Handle outside clicks for search 2
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        search2WrapperRef.current &&
        !search2WrapperRef.current.contains(e.target)
      ) {
        const isSuggestionClick =
          e.target.closest(".suggestion-box") ||
          e.target.closest(".suggestion-item") ||
          e.target.textContent === "View More";

        if (!isSuggestionClick) {
          setTimeout(() => {
            setSearch2Suggestions([]);
            setSearch2DropdownVisible(false);
          }, 100);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const getCompanyProfileUrl = (managerData) => {
    const companyProfile = managerData.company_logo_url || "";
    return companyProfile;
  };


  // Get top 3 tags (combining positive and negative)
  const getTopTags = (managerData) => {
    if (!managerData) return [];

    const positiveTags = managerData.top_positive_tags || [];
    const negativeTags = managerData.top_negative_tags || [];
    const allTags = [...positiveTags, ...negativeTags];

    return allTags.slice(0, 3);
  };

  // Get review count
  const getReviewCount = (managerData) => {
    if (!managerData) return 0;
    return managerData.reviews?.length || 0;
  };

  // Get rating
  const getRating = (managerData) => {
    if (!managerData) return 0;
    return managerData.manager_average_rating === 0
      ? 5
      : managerData.manager_average_rating;
  };

  return (
    <div className="compare-managers-container">
      <h2 className="compare-managers-title">Compare Managers</h2>

      <div className="compare-managers-search-container">
        <div
          className={`compare-managers-search-wrapper ${
            manager1 ? "has-manager" : ""
          } ${search1Term.trim().length > 0 ? "is-typing" : ""}`}
          ref={search1WrapperRef}
        >
          <SearchBar
            searchType="managers"
            searchTerm={search1Term}
            onSearchChange={handleSearch1Change}
            onSearchRequest={handleSearch1Request}
            suggestions={search1Suggestions}
            onSuggestionClick={handleSearch1SuggestionClick}
            onViewMore={handleSearch1ViewMore}
            lastKey={search1LastKey}
            remainingCompanies={[]}
            isDropdownOpen={search1IsDropdownOpen}
            onDropdownToggle={() =>
              setSearch1IsDropdownOpen(!search1IsDropdownOpen)
            }
            onDropdownChange={() => {}}
            dropdownVisible={search1DropdownVisible}
            showDropdown={false}
            isSmallScreen={false}
            className="compare-managers-search-input"
            placeholder="Search Managers #1"
            ref={search1Ref}
            dropdownRef={search1DropdownRef}
          />
        </div>

        <div
          className={`compare-managers-search-wrapper ${
            manager2 ? "has-manager" : ""
          } ${search2Term.trim().length > 0 ? "is-typing" : ""}`}
          ref={search2WrapperRef}
        >
          <SearchBar
            searchType="managers"
            searchTerm={search2Term}
            onSearchChange={handleSearch2Change}
            onSearchRequest={handleSearch2Request}
            suggestions={search2Suggestions}
            onSuggestionClick={handleSearch2SuggestionClick}
            onViewMore={handleSearch2ViewMore}
            lastKey={search2LastKey}
            remainingCompanies={[]}
            isDropdownOpen={search2IsDropdownOpen}
            onDropdownToggle={() =>
              setSearch2IsDropdownOpen(!search2IsDropdownOpen)
            }
            onDropdownChange={() => {}}
            dropdownVisible={search2DropdownVisible}
            showDropdown={false}
            isSmallScreen={false}
            className="compare-managers-search-input"
            placeholder="Search Managers #2"
            ref={search2Ref}
            dropdownRef={search2DropdownRef}
          />
        </div>
      </div>

      {/* Single Manager Info - Show when only one manager is selected */}
      {((manager1Data && !manager2Data) || (!manager1Data && manager2Data)) && (
        <div className="compare-managers-single-info">
          <div className="compare-managers-single-card">
            <div className="compare-managers-single-header">
              <h3>{manager1Data ? manager1?.name : manager2?.name}</h3>
              <img
                src={getCompanyProfileUrl(manager1Data || manager2Data)}
                // alt={`${item?.company_name} logo`}
                style={{
                  height: "24px",
                  width: "auto",
                  borderRadius: "50%",
                  marginLeft: "2px",
                  marginRight: "8px",
                  objectFit: "contain",
                }}
              />
            </div>
            <div className="compare-managers-single-content">
              <div className="compare-managers-single-stat">
                <span className="compare-managers-single-label">Rating:</span>
                <span className="compare-managers-single-value">
                  {getRating(manager1Data || manager2Data)}
                </span>
              </div>
              <div className="compare-managers-single-stat">
                <span className="compare-managers-single-label">Reviews:</span>
                <span className="compare-managers-single-value">
                  {getReviewCount(manager1Data || manager2Data)}
                </span>
              </div>
              {getTopTags(manager1Data || manager2Data).length > 0 && (
                <div className="compare-managers-single-stat">
                  <span className="compare-managers-single-label">Top Tags:</span>
                  <span className="compare-managers-single-value">
                    {getTopTags(manager1Data || manager2Data).join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="compare-managers-single-message">
            <p>Add another manager to compare and see detailed side-by-side analysis.</p>
          </div>
        </div>
      )}

      {/* Comparison Cards */}
      {manager1Data && manager2Data && (
        <div className="compare-managers-cards-container">
          <div className="compare-managers-card">
            <div className="compare-managers-card-header">
              <img
                src={manager1?.company_logo_url || "Company Logo"}
                // alt={`${item?.company_name} logo`}
                style={{
                  height: "24px",
                  width: "auto",
                  borderRadius: "50%",
                  marginLeft: "2px",
                  marginRight: "8px",
                  objectFit: "contain",
                }}
              />
              {manager1?.name || "Manager #1"}
            </div>
            <div className="compare-managers-card-row compare-managers-row-teal">
              {getRating(manager1Data)}
            </div>
            <div className="compare-managers-card-row compare-managers-row-yellow">
              {getReviewCount(manager1Data)}
            </div>
            {getTopTags(manager1Data).map((tag, index) => (
              <div
                key={index}
                className={`compare-managers-card-row ${
                  index % 2 === 0
                    ? "compare-managers-row-teal"
                    : "compare-managers-row-yellow"
                }`}
              >
                {tag}
              </div>
            ))}
            {/* Fill empty tag slots if less than 3 tags */}
            {getTopTags(manager1Data).length < 3 &&
              Array.from({ length: 3 - getTopTags(manager1Data).length }).map(
                (_, index) => (
                  <div
                    key={`empty-${index}`}
                    className={`compare-managers-card-row ${
                      (getTopTags(manager1Data).length + index) % 2 === 0
                        ? "compare-managers-row-teal"
                        : "compare-managers-row-yellow"
                    }`}
                  >
                    -
                  </div>
                )
              )}
          </div>

          <div className="compare-managers-labels-column">
            <div className="compare-managers-label-row compare-managers-label-header"></div>
            <div className="compare-managers-label-row">rating</div>
            <div className="compare-managers-label-row"># of reviews</div>
            <div className="compare-managers-label-row">top tag #1</div>
            <div className="compare-managers-label-row">top tag #2</div>
            <div className="compare-managers-label-row">top tag #3</div>
          </div>

          <div className="compare-managers-card">
            <div
              className="compare-managers-card-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",   // 👈 centers horizontally
                gap: "8px",
              }}
            >
              <img
                src={manager2?.company_logo_url || "Company Logo"}
                style={{
                  height: "24px",
                  width: "auto",
                  borderRadius: "50%",
                  objectFit: "contain",
                }}
              />
              {manager2?.name || "Manager #2"}
            </div>
            <div className="compare-managers-card-row compare-managers-row-yellow">
              {getRating(manager2Data)}
            </div>
            <div className="compare-managers-card-row compare-managers-row-teal">
              {getReviewCount(manager2Data)}
            </div>
            {getTopTags(manager2Data).map((tag, index) => (
              <div
                key={index}
                className={`compare-managers-card-row ${
                  index % 2 === 0
                    ? "compare-managers-row-yellow"
                    : "compare-managers-row-teal"
                }`}
              >
                {tag}
              </div>
            ))}
            {/* Fill empty tag slots if less than 3 tags */}
            {getTopTags(manager2Data).length < 3 &&
              Array.from({ length: 3 - getTopTags(manager2Data).length }).map(
                (_, index) => (
                  <div
                    key={`empty-${index}`}
                    className={`compare-managers-card-row ${
                      (getTopTags(manager2Data).length + index) % 2 === 0
                        ? "compare-managers-row-yellow"
                        : "compare-managers-row-teal"
                    }`}
                  >
                    -
                  </div>
                )
              )}
          </div>
        </div>
      )}

      {/* Loading states */}
      {(manager1Loading || manager2Loading) && (
        <div className="compare-managers-loading">Loading...</div>
      )}
    </div>
  );
};

export default CompareManagers;
